/**
 * Generatore firma email con AI: l'utente compila pochi campi e sceglie uno stile,
 * l'AI produce la firma (HTML + testo). Anteprima + "Usa questa firma".
 */
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, Loader2, Wand2, ImagePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { signatureApi, type SignatureStyle } from '@/lib/api/endpoints';
import { ApiError, API_URL, tokenStore } from '@/lib/api/client';

const STYLES: { id: SignatureStyle; label: string; hint: string }[] = [
  { id: 'minimal', label: 'Minimal', hint: 'Pulito, poche righe' },
  { id: 'corporate', label: 'Corporate', hint: 'Sobrio, professionale' },
  { id: 'creative', label: 'Creative', hint: 'Colorato, con icone' },
  { id: 'tech', label: 'Tech', hint: 'Moderno, startup' },
];

export function SignatureGenerator({
  defaultName,
  onGenerated,
}: {
  defaultName?: string;
  onGenerated: (plainText: string, html: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState(defaultName ?? '');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [style, setStyle] = useState<SignatureStyle>('minimal');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function uploadLogo(file: File) {
    if (!file.type.startsWith('image/')) { toast.error('Seleziona un’immagine'); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = await tokenStore.getAccess();
      const res = await fetch(`${API_URL}/ai/signature/logo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) throw new Error('upload');
      const { logo_url } = (await res.json()) as { logo_url: string };
      setLogoUrl(logo_url);
      toast.success('Logo caricato');
    } catch {
      toast.error('Caricamento logo fallito');
    } finally {
      setUploadingLogo(false);
    }
  }

  const genMut = useMutation({
    mutationFn: () =>
      signatureApi.generate({
        full_name: fullName.trim(),
        role: role.trim() || undefined,
        company: company.trim() || undefined,
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        logo_url: logoUrl || undefined,
        style,
      }),
    onSuccess: (r) => {
      setPreviewHtml(r.html);
      onGenerated(r.plain_text, r.html);
      toast.success('Firma generata, ricordati di salvare le preferenze');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Generazione fallita'),
  });

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="mt-3">
        <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Genera con AI
      </Button>
    );
  }

  return (
    <div className="mt-3 rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center gap-1.5 text-sm font-medium text-primary">
        <Wand2 className="h-4 w-4" /> Genera firma con l'AI
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Nome e cognome *</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Mario Rossi" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Ruolo</Label>
          <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Titolare" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Azienda</Label>
          <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Blagoweb S.r.l." />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Telefono</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-muted-foreground">Sito web</Label>
          <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.blagoweb.com" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-muted-foreground">Logo aziendale</Label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); e.target.value = ''; }}
          />
          {logoUrl ? (
            <div className="mt-1 flex items-center gap-3 rounded-md border bg-white p-2">
              <img src={logoUrl} alt="logo" className="h-10 max-w-[140px] object-contain" />
              <button type="button" className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" onClick={() => setLogoUrl(null)}>
                <X className="h-3.5 w-3.5" /> Rimuovi
              </button>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}>
              {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5 mr-1.5" />}
              Carica logo
            </Button>
          )}
        </div>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Stile</Label>
        <div className="mt-1 grid grid-cols-4 gap-2">
          {STYLES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStyle(s.id)}
              className={`rounded-md border px-2 py-1.5 text-left transition-colors ${
                style === s.id ? 'border-primary bg-primary/5' : 'hover:bg-accent'
              }`}
            >
              <div className="text-xs font-medium">{s.label}</div>
              <div className="text-[10px] text-muted-foreground leading-tight">{s.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => genMut.mutate()} disabled={!fullName.trim() || genMut.isPending}>
          {genMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          {previewHtml ? 'Rigenera' : 'Genera firma'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Chiudi</Button>
      </div>

      {previewHtml && (
        <div>
          <Label className="text-xs text-muted-foreground">Anteprima</Label>
          <div
            className="mt-1 rounded-md border bg-white p-3 text-sm overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Applicata qui sopra. Premi <strong>Salva preferenze</strong> per renderla definitiva.
          </p>
        </div>
      )}
    </div>
  );
}
